import { useState } from 'react'
import { useEditor } from '../store/useEditor'
import { sectionsOf } from '../lib/sections'
import { estimate, rateIsSet, scopePackages, SEASON_TIERS } from '../lib/estimate'

import type { SeasonTier } from '../types/pricing'
import { money } from '../lib/quote'

/**
 * YOUR PRICE, EXPLAINED.
 *
 * The arithmetic in full: feet times the rate, the seasonal discount as a
 * subtraction, and the total. Nothing about it is new - the rate is the
 * account's own roofPerFoot and the ladder is the existing one with its
 * existing percentages - and nothing about it is hidden.
 *
 * THE FOOTAGE IS MEASURED, WHICH IS WHY THIS EXISTS AT ALL. The editor has
 * refused to show a price since it was written, for a reason written in the
 * panel it hid: a confident figure derived from one reference length in one
 * plane of a photograph is exactly what not to put in front of a customer.
 * That was right. It stopped being true the day the footage started coming
 * from somebody standing at the house with a wheel, and the line under the
 * total still says how much of it is a guess.
 *
 * A RATE NOBODY SET IS NOT A PRICE. The shipped default is 1, a placeholder
 * rather than a dollar, and a house priced at a dollar a foot is not a
 * business. Until it is set this screen shows the footage and asks for the
 * rate rather than showing a number that looks like a decision.
 */
export default function Estimate() {
  const doc = useEditor((s) => s.doc)
  const setRate = useEditor((s) => s.setRate)
  const setTier = useEditor((s) => s.setTier)

  const sections = sectionsOf(doc)
  /*
   * Read defensively. upgradeDocument fills this in now, but a screen that
   * white-screens the whole app over one missing field is a screen that
   * trusted its caller too much.
   */
  const perFoot = doc.pricing?.rates?.roofPerFoot ?? 0
  /** What is in the rate field while somebody is typing in it. */
  const [typing, setTyping] = useState<string | null>(null)

  const tier = doc.pricing?.tier ?? 'nov'
  const e = estimate(sections, perFoot, tier)
  const packs = scopePackages(sections, perFoot, tier)

  if (sections.length === 0) {
    return (
      <div className="card pad">
        <h2>Nothing to price yet</h2>
        <p>
          Add the places on this house and a length for each, then the price follows from what was
          measured.
        </p>
      </div>
    )
  }

  return (
    <div className="dq-grid">
      <div className="est-left">
        {packs.length > 0 && (
          <div className="packages">
            {packs.map((p) => (
              <div key={p.id} className={p.id === 'complete' ? 'package selected' : 'package'}>
                <p className="ws-eyebrow">{p.id === 'complete' ? 'Complete front' : 'Front entrance'}</p>
                <h3>{p.name}</h3>
                <p className="small">{p.covers}</p>
                <div className="metric">
                  {p.estimate.priced ? money(p.estimate.total) : `${p.estimate.feet} ft`}
                </div>
                {/*
                  With a rate, the figure above is money and this line says
                  how many feet and which tier made it. Without one, the
                  figure above IS the feet, so this line was printing the same
                  number again with "no rate set" after it - and the amber
                  panel below already says that, in the words that tell you
                  what to do about it. Five copies of "no rate" on one screen
                  is not five warnings, it is one warning shouted.
                */}
                {p.estimate.priced && (
                  <p className="small">
                    {p.estimate.feet} ft · {p.estimate.discountPct}%{' '}
                    {p.estimate.tierLabel.toLowerCase()}
                  </p>
                )}

              </div>
            ))}
          </div>
        )}

        {/*
          THE LADDER, WHERE THE PRICE IS. Early booking is the close, so all
          three months are on screen: a customer can see what waiting costs.
        */}
        <div className="card pad">
          <h2>Seasonal pricing</h2>
          <p>Keep the ladder. The chosen install month determines the applied tier.</p>
          <div className="fields">
            <label>
              Installation month
              <select value={tier} onChange={(ev) => setTier(ev.target.value as SeasonTier)}>
                {SEASON_TIERS.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label} ({t.discountPct}% off)
                  </option>
                ))}
              </select>
            </label>
            <label>
              Price per installed foot
              {/*
                EMPTY MEANS UNSET, because that is what the screen says.

                The shipped default is 1, treated as a placeholder rather than
                a dollar, so this field printed "1" directly above a panel
                saying no rate is set. Both are the app's own doing and they
                contradict each other; a person reading them has to guess
                which one is lying.

                The typed value is kept as text while the field has focus, so
                typing the 1 of 12 does not clear the field out from under
                the person doing it.
              */}
              <input
                type="number"
                min={0}
                step={0.5}
                placeholder="Dollars per foot"
                value={typing ?? (rateIsSet(perFoot) ? String(perFoot) : '')}
                onFocus={() => setTyping(rateIsSet(perFoot) ? String(perFoot) : '')}
                onBlur={() => setTyping(null)}
                onChange={(ev) => {
                  setTyping(ev.target.value)
                  setRate('roofPerFoot', Number(ev.target.value) || 0)
                }}
              />

            </label>
          </div>
          {!e.priced && (
            <p className="notice">
              No rate is set, so nothing here is a price. Put the dollars per installed foot you
              actually sell at into the field above.
            </p>
          )}
        </div>
      </div>

      <div className="card pad dq-side">
        <p className="ws-eyebrow">Draft estimate</p>
        <h2>Your price, explained.</h2>

        <div className="impact-list">
          <div>
            <span>
              {e.feet} ft &times; {e.priced ? money(e.perFoot) : 'no rate'}
            </span>
            <b className="num">{e.priced ? money(e.subtotal) : '--'}</b>
          </div>
          {e.discountPct > 0 && (
            <div>
              <span>
                {e.tierLabel} · {e.discountPct}% off
              </span>
              <b className="num">{e.priced ? `-${money(e.discount)}` : '--'}</b>
            </div>
          )}
        </div>

        <div className="total-line big">
          <span>Quote total</span>
          <span className="num">{e.priced ? money(e.total) : '--'}</span>
        </div>

        {/*
          THE HONEST LINE UNDER THE TOTAL. A price built partly on a length
          nobody walked is a price with a hole in it, and the hole is named.
        */}
        <p className={e.solid ? 'sec-gate ok' : 'sec-gate warn'}>
          {e.solid
            ? 'Every foot in this price was measured at the house.'
            : `${Math.round(e.unverifiedFeet)} of these ${Math.round(e.feet)} feet have not been measured at the house.`}
        </p>
        <p className="footnote">
          No tax is applied. Labor, travel and takedown are not in this figure; the quote document
          is where the full lines live.
        </p>
      </div>
    </div>
  )
}
